import React, { useEffect, useState, Suspense, lazy } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Box, Button, CircularProgress, Alert } from '@mui/material';
import InitialData from '../../../InitialData';
import ApartmentCard from './ApartmentCard';
import ModalBooking from '../../components/ModalBooking';

const ModalBookingComponent = lazy(() =>
  Promise.resolve(ModalBooking).then((module) => ({ default: module })),
);

const fetchApartmentDataBySlug = (slug) => {
  const slugify = (value) => {
    if (!value) {
      return '';
    }
    return String(value)
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  };

  return InitialData.find((apt) => {
    const apartmentSlug = slugify(
      apt.slug || apt.name || apt.longName || apt.id,
    );
    return apartmentSlug === slug;
  });
};

export default function ApartmentDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [apartment, setApartment] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    const fetchedApartment = fetchApartmentDataBySlug(slug);
    if (fetchedApartment) {
      setApartment(fetchedApartment);
      setNotFound(false);
    } else {
      setNotFound(true);
    }
    setIsLoading(false);
  }, [slug]);

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleBack = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <Container
        component="section"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (notFound) {
    return (
      <Container component="section" sx={{ my: 5 }}>
        <Alert severity="error">
          {t('common.notFound', 'Apartment not found')}
        </Alert>
        <Button variant="contained" onClick={handleBack} sx={{ mt: 2 }}>
          {t('common.back', 'Back to Home')}
        </Button>
      </Container>
    );
  }

  return (
    <Container
      component="section"
      sx={{ my: { xs: 3, sm: 5 }, px: { xs: 2, sm: 3 } }}
    >
      {apartment && (
        <Box>
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <Button variant="outlined" onClick={handleBack}>
              ← {t('common.back', 'Back')}
            </Button>
          </Box>

          <ApartmentCard apartment={apartment} t={t} i18n={i18n} />

          <Box
            display="flex"
            justifyContent="center"
            my={{ xs: 2, sm: 3 }}
            gap={2}
          >
            <Button
              variant="contained"
              sx={{
                width: { xs: '100%', sm: 'auto' },
                px: 5,
                py: 2,
              }}
              color="secondary"
              onClick={handleOpenModal}
            >
              {t('apModal.bookNow')}
            </Button>
          </Box>

          <Suspense fallback={null}>
            <ModalBooking
              open={isModalOpen}
              onClose={handleCloseModal}
              apartment={apartment}
            />
          </Suspense>
        </Box>
      )}
    </Container>
  );
}
